import {makeAutoObservable, runInAction} from "mobx";
import OnboardingDataStore, {
    IGetMerchantOnboardingModel,
    IMerchantOnboardingChanges,
    IOnboardingScreenStore
} from "./onboarding-data-store";
import {IMerchantOnboarding} from "../onboarding-types";
import {ONBOARDING_STEPS, RESTOCK_LOCATION_TAB, RETURN_LOCATION_TAB} from "../constants";
import {IPlatformLocation, IReturnMethodAddress} from "../../return-methods/return-method-types";
import {OnboardingNavigationStore} from "./onboarding-navigation-store";
import ReturnAddressStore from "../../return-methods/stores/return-address-store";
import {getNullableChangesOrModel, isSet} from "../../../models/model-types-methods";
import {DROP_OFF_LOCATION} from "../../return-methods/constants";
import {IOption} from "../../../components/inputs/select-dropdown/select-dropdown";

class CreateReturnMethodScreenStore implements IOnboardingScreenStore {
    private _changes: IMerchantOnboardingChanges = {};
    private _getModel: IGetMerchantOnboardingModel;
    private readonly _stepName: ONBOARDING_STEPS = ONBOARDING_STEPS.CREATE_RETURN_METHOD;
    private _addressTab: RETURN_LOCATION_TAB = RETURN_LOCATION_TAB.PLATFORM;
    private _useSameAddressForRestockAddress: boolean = false;
    private _returnAddressStore: ReturnAddressStore = new ReturnAddressStore();
    private _isLoading: boolean = false;

    constructor(private _onboardingDataStore: OnboardingDataStore, private _navigationStore: OnboardingNavigationStore) {
        this._getModel = this._onboardingDataStore.getModel;
        makeAutoObservable(this, {}, { autoBind: true });
    }

    public get isLoading(): boolean { return this._isLoading; }
    public get isCoveragePlanSelected(): boolean { return this._onboardingDataStore.isSelectedCoveragePlanId(); }
    public get useSameAddressForRestockAddress() { return this._useSameAddressForRestockAddress; }
    public get isUseSameAddressForRestockAddressDisabled(): boolean {
        return !isSet(this._returnAddressStore.locationId);
    }

    public setDefaults(): void {
        const defaultLocation = this.platformLocations[0];
        if (!defaultLocation) {
            return;
        }

        const isReturnAddressUnset = this._returnAddressStore.isEmpty();
        const isRestockLocationUnset = !this.shopifyRestockLocationId;

        if (isReturnAddressUnset && isRestockLocationUnset) {
            this._returnAddressStore.setSelectedAddress(defaultLocation)
            this.setShopifyRestockLocation(defaultLocation);
        }
        /** settin initial state for checkbox */
        this._useSameAddressForRestockAddress = this._returnAddressStore.locationId === this.shopifyRestockLocationId;
    }

    public onSetModel(): void {
        this.discardChanges();
        this._returnAddressStore.setModel(this._getModel()?.address);
        if (!this._returnAddressStore.isEmpty() && !this._returnAddressStore.locationId) {
            this.setAddressTab(RETURN_LOCATION_TAB.CUSTOM_ADDRESS);
        }
    }

    private _setSameRestockLocationAsReturnAddress(): void {
        const returnAddressLocation = this.platformLocations.find((location) => location.location_id === this._returnAddressStore.locationId);
        if (returnAddressLocation) {
            this.setShopifyRestockLocation(returnAddressLocation);
        }
    }

    public setUseSameAddressForRestockAddress(v: boolean) {
        this._useSameAddressForRestockAddress = v;
        if (!v) {
            return null;
        }
        this._setSameRestockLocationAsReturnAddress();
    }

    public get addressTab(): RETURN_LOCATION_TAB {
        return this._addressTab;
    }

    public get returnAddressStore(): ReturnAddressStore {
        return this._returnAddressStore;
    }

    public get shopifyRestockLocationId(): string | null {
        return getNullableChangesOrModel(this._changes.shopify_restock_location_id, this._getModel()?.shopify_restock_location_id);
    }

    public setAddressTab(v: RETURN_LOCATION_TAB): void {
        if (v === RETURN_LOCATION_TAB.CUSTOM_ADDRESS) {
            this._returnAddressStore.setLocationId(null);
            this.setUseSameAddressForRestockAddress(false); 
        }
        this._addressTab = v;
    }

    public setReturnAddressLocation(platformLocation: IPlatformLocation): void {
        this._returnAddressStore.setSelectedAddress(platformLocation);
        if (this.useSameAddressForRestockAddress) {
            this.setShopifyRestockLocation(platformLocation);
        }
    }

    public setShopifyRestockLocation(platformLocation: IPlatformLocation): void {
        this._changes.shopify_restock_location_id = platformLocation.location_id;
    }

    public get restockAddressTab(): RESTOCK_LOCATION_TAB {
        if (this.shopifyRestockLocationId === DROP_OFF_LOCATION.FULFILLMENT_LOCATION) {
            return RESTOCK_LOCATION_TAB.FULFILLMENT_LOCATION;
        }
        return RESTOCK_LOCATION_TAB.PLATFORM;
    }

    public setRestockAddressTab(v: RESTOCK_LOCATION_TAB): void {
        if (v === RESTOCK_LOCATION_TAB.FULFILLMENT_LOCATION) {
            this._changes.shopify_restock_location_id = DROP_OFF_LOCATION.FULFILLMENT_LOCATION;
            this.setUseSameAddressForRestockAddress(false); 
        }
        else {
            this._changes.shopify_restock_location_id = null;
            if (this.useSameAddressForRestockAddress) {
                this._setSameRestockLocationAsReturnAddress();
            }
        }
    }
    
    public get platformLocations(): IPlatformLocation[] {
        const locations = this._onboardingDataStore.getSetting()?.store_locations;
        if (!locations || Object.keys(locations).length === 0) {
            return [];
        }
        return Object.values(locations) as IPlatformLocation[];
    }
    

    public hasChanges(): boolean {
        const localChanges = Object.keys(this._changes).length > 0;
        const isReturnAddressChanged = this._returnAddressStore.hasChanges();
        return localChanges || isReturnAddressChanged;
    }

    public discardChanges(): void {
        this._changes = {};
        this._returnAddressStore.discardChanges();
    }

    
    public getUpdatedModel(): IMerchantOnboarding {
        return { 
            ...this._getModel(),
            ...this._changes,
            address: this._returnAddressStore.getUpdatedModel(),
        } as IMerchantOnboarding;
    }

    public getMixPanelData(): Record<string, any> {

        const address = this._returnAddressStore.getUpdatedModel();

        return {
            ...this._changes,
            address: address,
            return_address_type: address?.shopify_location_id ? RETURN_LOCATION_TAB.PLATFORM : RETURN_LOCATION_TAB.CUSTOM_ADDRESS,
            restocking_location_type: this.shopifyRestockLocationId === DROP_OFF_LOCATION.FULFILLMENT_LOCATION ? RESTOCK_LOCATION_TAB.FULFILLMENT_LOCATION : RESTOCK_LOCATION_TAB.PLATFORM,
            using_same_address_for_restock_address: address?.shopify_location_id === this.shopifyRestockLocationId
        };
    }

    public isComplete(): boolean {
        const address = this._returnAddressStore.getUpdatedModel();
        if (!address || this._returnAddressStore.isEmpty()) {
            return false;
        }
        return isSet(this.shopifyRestockLocationId) && this._addressStoreIsComplete(address);
    }

    private _addressStoreIsComplete(address: IReturnMethodAddress): boolean {
        if (isSet(address.shopify_location_id)) {
            return true;
        }
        return !!address.name && !!address.line_1 && !!address.country && !!address.city && !!address.postal_code && !!address.company;
    }

    public previousStep(): void {
        this._navigationStore.previousStep();
    }

    public async nextStep(): Promise<void> {
        this._isLoading = true;
        const isSuccess = await this._onboardingDataStore.save(this._stepName);
        runInAction(() => {
            if (isSuccess) {
                this._navigationStore.nextStep();
            }
            this._isLoading = false;
        })
    }

    public getCountriesOptions(): IOption[] {
        return this._onboardingDataStore.getSetting()?.countries_list?.map((country) => {
            return {
                label: country.name,
                value: country.code,
            }
        }) ?? [];
    }
}

export default CreateReturnMethodScreenStore;